import { AppstoreAddOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Form, InputNumber, Radio, Row, Tooltip } from 'antd';
import { useContext, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import type {
  BulkEditFormValues,
  BulkEditInitialValues,
} from 'app/typings/bulkEdit';
import { ChannelManager } from 'app/typings/channelManagers';
import type { ExtranetPackage } from 'app/typings/packages';

import type {
  FieldsDisplayValues,
  PackageDisplayValues,
  RoomDisplayValues,
} from '../BulkEdit';

import { DiscountPriceSwitch } from './DiscountPriceSwitch';
import { DiscountRateSwitch } from './DiscountRateSwitch';

type BulkEditValuesProps = {
  openValues: FieldsDisplayValues;
  initialValues: BulkEditInitialValues;
  formValues: BulkEditFormValues;
  toggleSwitch: (
    switchValue: boolean,
    section: keyof PackageDisplayValues | keyof RoomDisplayValues,
    packageId?: number
  ) => void;
  resetInitialValues: () => void;
  packages: ExtranetPackage[];
};

type PackageValuesMode = 'discountRate' | 'discountPrice';

export const BulkEditValues = ({
  openValues,
  initialValues,
  formValues,
  toggleSwitch,
  resetInitialValues,
  packages,
}: BulkEditValuesProps) => {
  const dayPackage = packages.some((pkg) => pkg.dayPackage);

  const { currencyInput } = useContext(CurrencyContext);
  const { hotelInfo } = useContext(HotelContext);

  const [packageValuesMode, setPackageValuesMode] =
    useState<PackageValuesMode>('discountRate');

  return (
    <section className="bulkEdit__body__values">
      {hotelInfo?.channelManager === ChannelManager.NONE && (
        <div className="values-wrapper">
          <h2>Room values</h2>
          <div className="values-container">
            <SwitchableInput
              name="stock-switch"
              value={openValues.stock}
              onChange={(value: boolean) => {
                toggleSwitch(value, 'stock');
              }}
              title={
                <div className="title_tooltip">
                  <h3>Stock per day</h3>
                  <Tooltip
                    title="Bookings already made will be subtracted from that value"
                    placement="top"
                  >
                    <InfoCircleOutlined color="#BBB" />
                  </Tooltip>
                </div>
              }
              subtitle={
                <p className="subtitle">
                  {`Number of ${
                    dayPackage ? 'stocks' : 'rooms'
                  } you want to sell for each day`}
                </p>
              }
              childNode={
                <>
                  <div className="inputContainer">
                    <Form.Item
                      shouldUpdate
                      name="stock"
                      initialValue={initialValues.stock}
                      rules={[
                        {
                          required: openValues.stock,
                          message:
                            'Stock is required if you activate the toggle',
                        },
                      ]}
                      noStyle={!(formValues?.stock === null)}
                    >
                      <InputNumber
                        precision={0}
                        disabled={!openValues.stock}
                        type="number"
                        min={0}
                        placeholder="Stock number"
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </Form.Item>
                  </div>
                </>
              }
            />
            <SwitchableInput
              name="bar-switch"
              value={openValues.bar}
              onChange={(value: boolean) => {
                toggleSwitch(value, 'bar');
              }}
              title={
                <h3 className="title_tooltip">
                  {dayPackage ? 'Public price /2 pers.' : 'BAR R/O (NA/NR)'}
                </h3>
              }
              subtitle={
                <p className="subtitle">
                  {dayPackage
                    ? 'The public price /2 pers. for this experience'
                    : 'Best Available Rate (NA/NR) for this room'}
                </p>
              }
              childNode={
                <Row className="inputContainer">
                  <Form.Item
                    name="bar"
                    initialValue={initialValues.bar}
                    rules={[
                      {
                        required: openValues.bar,
                        message: 'BAR is required if you activate the toggle',
                      },
                    ]}
                    noStyle={!(formValues?.bar === null)}
                  >
                    {currencyInput({
                      disabled: !openValues.bar,
                      min: 0,
                      placeholder: dayPackage ? 'Price /2 pers.' : 'BAR R/O',
                      onWheel: (e) => e.currentTarget.blur(),
                    })}
                  </Form.Item>
                </Row>
              }
            />
          </div>
        </div>
      )}
      <div className="values-wrapper">
        <div className="values-header">
          <Row justify={'space-between'} align={'middle'}>
            <h2>Package values</h2>
            <Radio.Group
              value={packageValuesMode}
              onChange={(e) => {
                resetInitialValues();
                setPackageValuesMode(e.target.value);
              }}
            >
              <Radio.Button value="discountPrice">Rate</Radio.Button>
              <Radio.Button value="discountRate">Discount</Radio.Button>
            </Radio.Group>
          </Row>
          {packageValuesMode === 'discountPrice' ? (
            <p className="description">
              Rates that will be published on Staycation for the whole package.
            </p>
          ) : (
            <p className="description">
              {dayPackage
                ? 'Discount applied to the room (impacts the Staycation rate).'
                : packages.length > 1
                  ? 'Discount that will be applied to the total value of the package (BAR + value of experiences included in the package).'
                  : 'Discount applied to the room (impacts the Staycation rate).'}
            </p>
          )}
        </div>
        <div className="values-container">
          {packageValuesMode === 'discountPrice' &&
            packages.map((pkg) => (
              <DiscountPriceSwitch
                key={`disount-price-switch-${pkg.id}`}
                openValues={openValues}
                formValues={formValues}
                pkg={pkg}
                toggleSwitch={toggleSwitch}
                initialDiscountPrice={initialValues?.[pkg.id]?.discountPrice}
              />
            ))}
          {packageValuesMode === 'discountRate' &&
            packages.map((pkg) => (
              <DiscountRateSwitch
                key={`disount-rate-switch-${pkg.id}`}
                openValues={openValues}
                initialDiscountRate={initialValues?.[pkg.id]?.discountRate}
                formValues={formValues}
                pkg={pkg}
                toggleSwitch={toggleSwitch}
              />
            ))}
          {!packages.length && (
            <EmptyState
              title="No room selected"
              description="You have to select a room to display package values"
              icon={<AppstoreAddOutlined />}
            />
          )}
        </div>
      </div>
    </section>
  );
};
